// Push Notification utility for Fish on Fire app

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return { success: false, permission: 'unsupported', message: 'Notifications not supported' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { 
      success: permission === 'granted', 
      permission,
      message: permission === 'granted' 
        ? 'Notifications enabled!' 
        : permission === 'denied' 
          ? 'Notifications blocked. Please enable in browser settings.' 
          : 'Notification permission dismissed'
    };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, permission: 'error', message: 'Failed to request permission' };
  }
};

// Show a local notification (when app is in foreground or via service worker)
export const showNotification = async (title, options = {}) => {
  const permission = getNotificationPermission();
  
  if (permission !== 'granted') {
    console.log('Notification permission not granted');
    return false;
  }
  
  const defaultOptions = {
    icon: '/icon-192x192.png',
    badge: '/icon-64x64.png',
    vibrate: [200, 100, 200],
    tag: 'fish-on-fire',
    renotify: true,
    ...options
  };
  
  try {
    // Try to use service worker for notification (works in background)
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, defaultOptions);
      return true;
    } else {
      // Fallback to regular Notification API
      new Notification(title, defaultOptions);
      return true;
    }
  } catch (error) {
    console.error('Error showing notification:', error);
    return false;
  }
};

// Order status notification messages
export const getOrderStatusNotification = (status, orderNumber) => {
  const notifications = {
    accepted: {
      title: 'Order Accepted! 🎉',
      body: `Your order #${orderNumber} has been accepted and will be prepared soon.`,
      tag: `order-${orderNumber}-accepted`
    },
    preparing: {
      title: 'Preparing Your Order 👨‍🍳',
      body: `Your order #${orderNumber} is being prepared with fresh ingredients.`,
      tag: `order-${orderNumber}-preparing`
    },
    ready: {
      title: 'Order Ready! 🍽️',
      body: `Your order #${orderNumber} is ready for pickup!`,
      tag: `order-${orderNumber}-ready`
    },
    out_for_delivery: {
      title: 'Out for Delivery! 🚚',
      body: `Your order #${orderNumber} is on the way to you!`,
      tag: `order-${orderNumber}-delivery`
    },
    delivered: {
      title: 'Order Delivered! ✅',
      body: `Your order #${orderNumber} has been delivered. Enjoy your meal!`,
      tag: `order-${orderNumber}-delivered`
    },
    completed: {
      title: 'Order Complete! ✅',
      body: `Your order #${orderNumber} is complete. Thank you for ordering!`,
      tag: `order-${orderNumber}-completed`
    },
    rejected: {
      title: 'Order Rejected ❌',
      body: `Sorry, your order #${orderNumber} couldn't be fulfilled. Refund will be processed.`,
      tag: `order-${orderNumber}-rejected`
    },
    cancelled: {
      title: 'Order Cancelled',
      body: `Your order #${orderNumber} has been cancelled.`,
      tag: `order-${orderNumber}-cancelled`
    }
  };
  
  return notifications[status] || {
    title: 'Order Update',
    body: `Your order #${orderNumber} status: ${status}`,
    tag: `order-${orderNumber}-${status}`
  };
};

// Send notification for order status change
export const notifyOrderStatus = async (status, orderNumber, orderId) => {
  const { title, body, tag } = getOrderStatusNotification(status, orderNumber);
  
  return showNotification(title, {
    body,
    tag,
    data: { orderId, status, url: '/orders' },
    requireInteraction: ['rejected', 'ready', 'delivered'].includes(status)
  });
};

// Subscribe to push notifications (for future server-push implementation)
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    return { success: false, message: 'Push notifications not supported' };
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Note: For real push notifications, you'd need a VAPID key from your server
      // This is a placeholder - real implementation would get the key from backend
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }
    
    return { success: true, subscription };
  } catch (error) {
    console.error('Push subscription failed:', error);
    return { success: false, message: error.message };
  }
};

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification,
  notifyOrderStatus,
  subscribeToPush
};
